import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle, ChevronRight, ChevronLeft, User, Package, Palette, Ruler, MessageSquare, Copy } from 'lucide-react';
import { SPORTS, SHIRT_STYLES, type OrderForm } from '@/lib/index';
import { generateFolio, saveOrder } from '@/lib/orders';
import { sendToGoogleSheets } from '@/lib/config';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

const schema = z.object({
  clientName: z.string().min(2, 'Ingresa tu nombre completo'),
  phone: z.string().min(10, 'Teléfono de 10 dígitos').max(15),
  email: z.string().email('Correo inválido').or(z.literal('')),
  city: z.string().min(2, 'Ingresa tu ciudad'),
  state: z.string().min(2, 'Ingresa tu estado'),
  sport: z.string().min(1, 'Selecciona el deporte o uso'),
  teamName: z.string().min(1, 'Ingresa el nombre del equipo o empresa'),
  quantity: z.coerce.number().min(1, 'Mínimo 1 pieza').max(9999),
  shirtStyle: z.string().min(1, 'Selecciona el estilo'),
  colors: z.string().min(2, 'Describe los colores que deseas'),
  hasLogo: z.string().min(1, 'Selecciona una opción'),
  logoDescription: z.string().optional(),
  hasNumbers: z.string().min(1, 'Selecciona una opción'),
  hasNames: z.string().min(1, 'Selecciona una opción'),
  sizes: z.string().min(2, 'Describe las tallas necesarias'),
  referenceDescription: z.string().optional(),
  deliveryDate: z.string().optional(),
  additionalNotes: z.string().optional(),
});

const STEPS = [
  { id: 1, label: 'Datos del cliente', icon: User },
  { id: 2, label: 'Datos del pedido', icon: Package },
  { id: 3, label: 'Diseño y colores', icon: Palette },
  { id: 4, label: 'Tallas y entrega', icon: Ruler },
  { id: 5, label: 'Confirmación', icon: MessageSquare },
];

function StepIndicator({ currentStep }: { currentStep: number }) {
  return (
    <div className="flex items-center justify-between mb-10 overflow-x-auto pb-2">
      {STEPS.map((step, i) => {
        const Icon = step.icon;
        const isActive = step.id === currentStep;
        const isDone = step.id < currentStep;
        return (
          <div key={step.id} className="flex items-center">
            <div className="flex flex-col items-center gap-1 min-w-[60px]">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-sm transition-all duration-300 ${
                  isDone
                    ? 'bg-primary text-primary-foreground'
                    : isActive
                    ? 'bg-primary/20 border-2 border-primary text-primary'
                    : 'bg-muted text-muted-foreground border border-border'
                }`}
              >
                {isDone ? <CheckCircle size={18} /> : <Icon size={16} />}
              </div>
              <span className={`text-[9px] font-bold tracking-wider text-center leading-tight hidden md:block ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>
                {step.label.toUpperCase()}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mx-2 transition-colors duration-300 min-w-[20px] md:min-w-[40px] ${isDone ? 'bg-primary' : 'bg-border'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function Order() {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [currentFolio, setCurrentFolio] = useState('');
  const [copied, setCopied] = useState(false);
  const [logoFile, setLogoFile] = useState<string | null>(null);
  const [logoFileName, setLogoFileName] = useState('');

  const { register, handleSubmit, formState: { errors }, watch, setValue, trigger, getValues } = useForm<OrderForm>({
    resolver: zodResolver(schema),
    defaultValues: { quantity: 1, hasLogo: '', hasNumbers: '', hasNames: '' },
  });

  const hasLogo = watch('hasLogo');
  const quantity = watch('quantity') ?? 1;

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setLogoFile(ev.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const getPrice = (q: number) => {
    if (q >= 400) return 'Consultar precio especial';
    if (q >= 100) return `$${240 * q} (${q} pzs × $240 c/u)`;
    if (q >= 20) return `$${260 * q} (${q} pzs × $260 c/u)`;
    if (q >= 2) return `$${290 * q} (${q} pzs × $290 c/u)`;
    return `$350 (precio unitario)`;
  };

  const validateStep = async () => {
    const fieldsByStep: Record<number, (keyof OrderForm)[]> = {
      1: ['clientName', 'phone', 'city', 'state'],
      2: ['sport', 'teamName', 'quantity', 'shirtStyle'],
      3: ['colors', 'hasLogo', 'hasNumbers', 'hasNames'],
      4: ['sizes'],
    };
    const fields = fieldsByStep[step] || [];
    return await trigger(fields);
  };

  const next = async () => {
    const valid = await validateStep();
    if (valid) setStep((s) => Math.min(s + 1, STEPS.length));
  };
  const prev = () => setStep((s) => Math.max(s - 1, 1));

  const onSubmit = (data: OrderForm) => {
    const folio = generateFolio();
    setCurrentFolio(folio);
    const now = new Date();
    const fecha = now.toLocaleDateString('es-MX');
    const hora = now.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    const precio = getPrice(data.quantity);

    // Guardar en localStorage
    saveOrder({
      folio,
      fecha,
      hora,
      clientName: data.clientName,
      phone: data.phone,
      email: data.email || '',
      city: data.city,
      state: data.state,
      sport: data.sport,
      teamName: data.teamName,
      quantity: data.quantity,
      shirtStyle: data.shirtStyle,
      estimatedPrice: precio,
      colors: data.colors,
      hasLogo: data.hasLogo,
      logoDescription: data.logoDescription || '',
      logoImageBase64: logoFile || '',
      hasNumbers: data.hasNumbers,
      hasNames: data.hasNames,
      sizes: data.sizes,
      deliveryDate: data.deliveryDate || '',
      referenceDescription: data.referenceDescription || '',
      additionalNotes: data.additionalNotes || '',
      status: 'Nuevo',
    });

    const waText = encodeURIComponent(
      `🏆 *NUEVO PEDIDO - DOS DE TRES*\n` +
      `📋 *FOLIO: ${folio}*\n📅 ${fecha} ${hora}\n\n` +
      `👤 *CLIENTE:*\n• Nombre: ${data.clientName}\n• Teléfono: ${data.phone}\n• Email: ${data.email || 'No indicado'}\n• Ciudad: ${data.city}, ${data.state}\n\n` +
      `📦 *PEDIDO:*\n• Deporte/Uso: ${data.sport}\n• Equipo/Empresa: ${data.teamName}\n• Cantidad: ${data.quantity} piezas\n• Estilo: ${data.shirtStyle}\n\n` +
      `🎨 *DISEÑO:*\n• Colores: ${data.colors}\n• Logo: ${data.hasLogo}${data.logoDescription ? ' - ' + data.logoDescription : ''}\n• Números: ${data.hasNumbers}\n• Nombres: ${data.hasNames}\n\n` +
      `📐 *TALLAS Y ENTREGA:*\n• Tallas: ${data.sizes}\n• Fecha deseada: ${data.deliveryDate || 'A convenir'}\n\n` +
      `💬 *NOTAS:*\n${data.additionalNotes || 'Ninguna'}\n\n` +
      `💰 *ESTIMADO:* ${precio}`
    );
    window.open(`https://wa.me/528333007124?text=${waText}`, '_blank');
    // Enviar a Google Sheets en paralelo (sin bloquear)
    sendToGoogleSheets({
      folio, fecha, hora, status: 'Nuevo',
      clientName: data.clientName, phone: data.phone, email: data.email || '',
      city: data.city, state: data.state, sport: data.sport,
      teamName: data.teamName, quantity: data.quantity, shirtStyle: data.shirtStyle,
      estimatedPrice: precio, colors: data.colors, hasLogo: data.hasLogo,
      logoDescription: data.logoDescription || '', hasNumbers: data.hasNumbers,
      hasNames: data.hasNames, sizes: data.sizes, deliveryDate: data.deliveryDate || '',
      referenceDescription: data.referenceDescription || '', additionalNotes: data.additionalNotes || '',
    });
    setSubmitted(true);
  };

  const copyFolio = () => {
    navigator.clipboard.writeText(currentFolio);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center py-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="text-center max-w-md mx-auto px-4"
        >
          <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-primary" size={40} />
          </div>
          <h2 className="text-3xl font-black uppercase tracking-widest text-foreground mb-4">¡PEDIDO ENVIADO!</h2>

          {/* FOLIO destacado */}
          <div className="bg-card border border-primary rounded-xl p-5 mb-6">
            <p className="text-[10px] font-bold tracking-widest text-muted-foreground uppercase mb-2">Número de Folio</p>
            <p className="text-2xl font-black font-mono text-primary mb-3">{currentFolio}</p>
            <button
              onClick={copyFolio}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/30 text-primary text-xs font-bold tracking-widest rounded hover:bg-primary/20 transition-all"
            >
              <Copy size={12} />
              {copied ? '¡COPIADO!' : 'COPIAR FOLIO'}
            </button>
            <p className="text-[10px] text-muted-foreground mt-3">Guarda este número para dar seguimiento a tu pedido.</p>
          </div>

          <p className="text-muted-foreground mb-8 leading-relaxed">
            Tu pedido fue enviado a WhatsApp con el folio incluido. Un asesor de <strong className="text-primary">Dos de Tres</strong> te contactará para confirmar los detalles.
          </p>
          <button
            onClick={() => { setSubmitted(false); setStep(1); }}
            className="px-8 py-3 bg-primary text-primary-foreground font-black text-xs tracking-widest uppercase rounded hover:bg-primary/90 transition-all"
          >
            HACER OTRO PEDIDO
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-16">
      <div className="container mx-auto px-4 max-w-2xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 40 }}
          className="text-center mb-10"
        >
          <p className="text-primary font-bold text-xs tracking-widest uppercase mb-2">FORMULARIO DE PEDIDO</p>
          <h1 className="text-3xl md:text-4xl font-black uppercase tracking-tight text-foreground">
            PERSONALIZA TU<br /><span className="text-primary">UNIFORME</span>
          </h1>
          <p className="text-muted-foreground mt-3 text-sm">
            Completa el formulario y recibe tu cotización al instante por WhatsApp.
          </p>
        </motion.div>

        <StepIndicator currentStep={step} />

        <form onSubmit={handleSubmit(onSubmit)}>
          <AnimatePresence mode="wait">
            {/* STEP 1: Cliente */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ type: 'spring', stiffness: 300, damping: 35 }}
                className="bg-card border border-border rounded-xl p-6 space-y-5"
              >
                <h3 className="text-lg font-black uppercase tracking-widest text-primary flex items-center gap-2">
                  <User size={18} /> DATOS DEL CLIENTE
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase">Nombre completo *</Label>
                    <Input {...register('clientName')} placeholder="Ej. Juan García" className="bg-muted border-input focus:border-primary" />
                    {errors.clientName && <p className="text-destructive text-xs">{errors.clientName.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase">Teléfono / WhatsApp *</Label>
                    <Input {...register('phone')} placeholder="Ej. 833 300 7124" className="bg-muted border-input focus:border-primary" />
                    {errors.phone && <p className="text-destructive text-xs">{errors.phone.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase">Correo electrónico</Label>
                    <Input {...register('email')} type="email" placeholder="tu@correo.com" className="bg-muted border-input focus:border-primary" />
                    {errors.email && <p className="text-destructive text-xs">{errors.email.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase">Ciudad *</Label>
                    <Input {...register('city')} placeholder="Ej. Tampico" className="bg-muted border-input focus:border-primary" />
                    {errors.city && <p className="text-destructive text-xs">{errors.city.message}</p>}
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-xs font-bold tracking-widest uppercase">Estado *</Label>
                    <Input {...register('state')} placeholder="Ej. Tamaulipas" className="bg-muted border-input focus:border-primary" />
                    {errors.state && <p className="text-destructive text-xs">{errors.state.message}</p>}
                  </div>
                </div>
              </motion.div>
            )}

            {/* STEP 2: Pedido */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ type: 'spring', stiffness: 300, damping: 35 }}
                className="bg-card border border-border rounded-xl p-6 space-y-5"
              >
                <h3 className="text-lg font-black uppercase tracking-widest text-primary flex items-center gap-2">
                  <Package size={18} /> DATOS DEL PEDIDO
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase">Deporte o uso *</Label>
                    <Select onValueChange={(v) => setValue('sport', v)}>
                      <SelectTrigger className="bg-muted border-input">
                        <SelectValue placeholder="Selecciona el deporte..." />
                      </SelectTrigger>
                      <SelectContent>
                        {SPORTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {errors.sport && <p className="text-destructive text-xs">{errors.sport.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase">Nombre del equipo o empresa *</Label>
                    <Input {...register('teamName')} placeholder="Ej. Águilas FC" className="bg-muted border-input focus:border-primary" />
                    {errors.teamName && <p className="text-destructive text-xs">{errors.teamName.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase">Cantidad de piezas *</Label>
                    <Input {...register('quantity')} type="number" min={1} placeholder="Ej. 20" className="bg-muted border-input focus:border-primary font-mono" />
                    {errors.quantity && <p className="text-destructive text-xs">{errors.quantity.message}</p>}
                    {quantity > 0 && (
                      <p className="text-primary text-xs font-bold font-mono mt-1">💰 {getPrice(quantity)}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase">Estilo de playera *</Label>
                    <Select onValueChange={(v) => setValue('shirtStyle', v)}>
                      <SelectTrigger className="bg-muted border-input">
                        <SelectValue placeholder="Selecciona el estilo..." />
                      </SelectTrigger>
                      <SelectContent>
                        {SHIRT_STYLES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {errors.shirtStyle && <p className="text-destructive text-xs">{errors.shirtStyle.message}</p>}
                  </div>
                </div>
              </motion.div>
            )}

            {/* STEP 3: Diseño */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ type: 'spring', stiffness: 300, damping: 35 }}
                className="bg-card border border-border rounded-xl p-6 space-y-6"
              >
                <h3 className="text-lg font-black uppercase tracking-widest text-primary flex items-center gap-2">
                  <Palette size={18} /> DISEÑO Y COLORES
                </h3>

                <div className="space-y-2">
                  <Label className="text-xs font-bold tracking-widest uppercase">Colores deseados *</Label>
                  <Input {...register('colors')} placeholder="Ej. Azul marino con detalles neón, blanco y negro..." className="bg-muted border-input focus:border-primary" />
                  {errors.colors && <p className="text-destructive text-xs">{errors.colors.message}</p>}
                </div>

                <div className="space-y-3">
                  <Label className="text-xs font-bold tracking-widest uppercase">¿Tienes logo o escudo? *</Label>
                  <RadioGroup
                    onValueChange={(v) => setValue('hasLogo', v)}
                    className="flex gap-4"
                  >
                    {['Sí, tengo logo', 'No tengo logo', 'Quiero que diseñen el logo'].map((opt) => (
                      <div key={opt} className="flex items-center space-x-2">
                        <RadioGroupItem value={opt} id={`logo-${opt}`} />
                        <Label htmlFor={`logo-${opt}`} className="text-sm cursor-pointer">{opt}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                  {errors.hasLogo && <p className="text-destructive text-xs">{errors.hasLogo.message}</p>}
                  {hasLogo && (
                    <div className="space-y-3 mt-2">
                      <Input
                        {...register('logoDescription')}
                        placeholder="Describe tu logo o cómo quieres que lo diseñen..."
                        className="bg-muted border-input focus:border-primary"
                      />
                      {/* Upload logo — solo si tiene logo */}
                      {hasLogo === 'Sí, tengo logo' && (
                        <div className="space-y-2">
                          <Label className="text-xs font-bold tracking-widest uppercase">Adjuntar logo / escudo</Label>
                          <label className="flex flex-col items-center justify-center w-full border-2 border-dashed border-primary/40 rounded-lg p-5 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all">
                            {logoFile ? (
                              <div className="flex flex-col items-center gap-2">
                                <img src={logoFile} alt="Logo preview" className="max-h-28 max-w-full object-contain rounded" />
                                <span className="text-[11px] text-primary font-bold">{logoFileName}</span>
                                <span className="text-[10px] text-muted-foreground">Clic para cambiar</span>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center gap-2 text-muted-foreground">
                                <svg className="w-8 h-8 text-primary/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 16v-8m0 0-3 3m3-3 3 3M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1" />
                                </svg>
                                <span className="text-xs font-bold text-primary">SUBIR LOGO</span>
                                <span className="text-[10px]">PNG, JPG, SVG · Máx. 5MB</span>
                              </div>
                            )}
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={handleLogoUpload}
                            />
                          </label>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-5">
                  <div className="space-y-3">
                    <Label className="text-xs font-bold tracking-widest uppercase">¿Llevan números? *</Label>
                    <RadioGroup onValueChange={(v) => setValue('hasNumbers', v)} className="flex gap-3 flex-wrap">
                      {['Sí', 'No'].map((opt) => (
                        <div key={opt} className="flex items-center space-x-2">
                          <RadioGroupItem value={opt} id={`num-${opt}`} />
                          <Label htmlFor={`num-${opt}`} className="text-sm cursor-pointer">{opt}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                    {errors.hasNumbers && <p className="text-destructive text-xs">{errors.hasNumbers.message}</p>}
                  </div>
                  <div className="space-y-3">
                    <Label className="text-xs font-bold tracking-widest uppercase">¿Llevan nombres? *</Label>
                    <RadioGroup onValueChange={(v) => setValue('hasNames', v)} className="flex gap-3 flex-wrap">
                      {['Sí', 'No'].map((opt) => (
                        <div key={opt} className="flex items-center space-x-2">
                          <RadioGroupItem value={opt} id={`names-${opt}`} />
                          <Label htmlFor={`names-${opt}`} className="text-sm cursor-pointer">{opt}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                    {errors.hasNames && <p className="text-destructive text-xs">{errors.hasNames.message}</p>}
                  </div>
                </div>
              </motion.div>
            )}

            {/* STEP 4: Tallas y entrega */}
            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ type: 'spring', stiffness: 300, damping: 35 }}
                className="bg-card border border-border rounded-xl p-6 space-y-5"
              >
                <h3 className="text-lg font-black uppercase tracking-widest text-primary flex items-center gap-2">
                  <Ruler size={18} /> TALLAS Y ENTREGA
                </h3>

                <div className="space-y-2">
                  <Label className="text-xs font-bold tracking-widest uppercase">Tallas requeridas *</Label>
                  <Textarea
                    {...register('sizes')}
                    placeholder="Ej. 5 talla S, 8 talla M, 5 talla L, 2 talla XL&#10;O indica: todas M, o escribe la lista completa con nombres y tallas"
                    className="bg-muted border-input focus:border-primary min-h-[100px] font-mono text-sm"
                  />
                  {errors.sizes && <p className="text-destructive text-xs">{errors.sizes.message}</p>}
                  <p className="text-[10px] text-muted-foreground">
                    Disponibles: XS, S, M, L, XL, XXL, XXXL. También hacemos tallas especiales.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold tracking-widest uppercase">¿Para cuándo lo necesitas?</Label>
                  <Input
                    {...register('deliveryDate')}
                    type="date"
                    className="bg-muted border-input focus:border-primary font-mono"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold tracking-widest uppercase">Descripción del diseño o referencia</Label>
                  <Textarea
                    {...register('referenceDescription')}
                    placeholder="Describe el diseño que imaginas, colores, estilo, o proporciona una referencia de imagen..."
                    className="bg-muted border-input focus:border-primary min-h-[80px]"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold tracking-widest uppercase">Notas adicionales</Label>
                  <Textarea
                    {...register('additionalNotes')}
                    placeholder="Alguna información adicional que quieras compartir con el equipo de Dos de Tres..."
                    className="bg-muted border-input focus:border-primary min-h-[80px]"
                  />
                </div>
              </motion.div>
            )}

            {/* STEP 5: Resumen */}
            {step === 5 && (
              <motion.div
                key="step5"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ type: 'spring', stiffness: 300, damping: 35 }}
                className="bg-card border border-border rounded-xl p-6 space-y-5"
              >
                <h3 className="text-lg font-black uppercase tracking-widest text-primary flex items-center gap-2">
                  <MessageSquare size={18} /> RESUMEN DEL PEDIDO
                </h3>
                {(() => {
                  const d = getValues();
                  const rows = [
                    { label: 'Cliente', value: d.clientName },
                    { label: 'Teléfono', value: d.phone },
                    { label: 'Ciudad / Estado', value: `${d.city}, ${d.state}` },
                    { label: 'Deporte / Uso', value: d.sport },
                    { label: 'Equipo / Empresa', value: d.teamName },
                    { label: 'Cantidad', value: `${d.quantity} piezas` },
                    { label: 'Estilo', value: d.shirtStyle },
                    { label: 'Colores', value: d.colors },
                    { label: 'Logo', value: d.hasLogo },
                    { label: 'Números', value: d.hasNumbers },
                    { label: 'Nombres', value: d.hasNames },
                    { label: 'Tallas', value: d.sizes },
                    { label: 'Fecha deseada', value: d.deliveryDate || 'A convenir' },
                  ];
                  return (
                    <>
                      <div className="space-y-2">
                        {rows.filter(r => r.value).map((r) => (
                          <div key={r.label} className="flex justify-between items-start py-2 border-b border-border last:border-0">
                            <span className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground w-32 shrink-0">{r.label}</span>
                            <span className="text-sm text-foreground text-right font-mono">{r.value}</span>
                          </div>
                        ))}
                      </div>
                      <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
                        <p className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-1">PRECIO ESTIMADO</p>
                        <p className="text-lg font-black text-primary font-mono">{getPrice(d.quantity)}</p>
                        <p className="text-[10px] text-muted-foreground mt-1">Un solo pedido / Un solo diseño</p>
                      </div>
                    </>
                  );
                })()}
                <p className="text-sm text-muted-foreground">
                  Al hacer clic en <strong className="text-primary">ENVIAR PEDIDO</strong>, se abrirá WhatsApp con tu información.
                  Un asesor confirmará los detalles y te enviará la cotización final.
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-6">
            {step > 1 ? (
              <Button type="button" variant="outline" onClick={prev} className="flex items-center gap-2 font-bold tracking-widest text-xs uppercase">
                <ChevronLeft size={16} /> ANTERIOR
              </Button>
            ) : (
              <div />
            )}
            {step < STEPS.length ? (
              <Button
                type="button"
                onClick={next}
                className="flex items-center gap-2 bg-primary text-primary-foreground font-black text-xs tracking-widest uppercase hover:bg-primary/90"
              >
                SIGUIENTE <ChevronRight size={16} />
              </Button>
            ) : (
              <Button
                type="submit"
                className="flex items-center gap-2 bg-primary text-primary-foreground font-black text-xs tracking-widest uppercase hover:bg-primary/90 px-8"
              >
                ENVIAR PEDIDO POR WHATSAPP <ChevronRight size={16} />
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
